import type {
  DurableChatTurnEvent,
  DurableChatTurnIdentity,
  DurableChatTurnSnapshot,
  DurableChatTurnStatus,
  DurableChatTurnStatusResponse,
} from "@/api/models"

import { getDurableChatTurnStatusApi } from "@/api/api"

import {
  DurableChatTurnSubscription,
  type DurableChatTurnSubscriptionOptions,
} from "./durable-chat-turn-subscription"

export type DurableChatTurnRegistryState = DurableChatTurnIdentity & {
  cursor: number
  events: DurableChatTurnEvent[]
  reconnecting: boolean
  snapshot: DurableChatTurnSnapshot
  status: DurableChatTurnStatus
  terminal: boolean
}

export type DurableChatTurnRegistryRegistration = DurableChatTurnIdentity & {
  getToken: () => string | undefined
  snapshot: DurableChatTurnSnapshot
  /**
   * True only when `snapshot` is a genuine, fully-reconstructed covering
   * snapshot — the backend actually read a stored snapshot document and
   * returned it (mirrors `DurableChatTurnDetailsResponse.replayGap !== null
   * && !== undefined`, the same signal `recover()` reads off
   * `DurableChatTurnStatusResponse`). Both current callers
   * (`sendDurableExplorerChatTurn`'s accept response and
   * `useDurableChatTurnHydration`'s `/turns/current` read) resolve this to
   * `false` today — neither backend DTO builder ever attaches a covering
   * snapshot document there (`build_details_dto` always calls
   * `build_snapshot_dto` with `snapshot_doc=None`, see
   * `services/chat_turn_service.py`) — but the flag is wired to that real
   * signal rather than hardcoded, so a future backend change flows through
   * automatically instead of silently staying dead.
   *
   * Governs two things:
   *  - a re-registration of an already-known target: `false` is an
   *    idempotent no-op that never blanks in-progress content or jumps the
   *    cursor past events the subscription is mid-replay of (issue #2513);
   *  - a brand-new entry's initial `cursor`: `true` seeds it at
   *    `snapshot.snapshotSeq` (the snapshot already covers everything up to
   *    there); `false` seeds it at `0` so the subscription opens at
   *    `afterSeq=0` and the backend's own replay/gap machinery reconstructs
   *    the full answer instead of only what arrives after this attach — a
   *    short/unpruned turn replays every event from the start, a long/pruned
   *    one reports `gap=true` and hands back the real covering snapshot
   *    (issue #2513 finding 2). Bounded by construction either way:
   *    `MAX_REPLAY_EVENTS` caps a from-zero WS replay, and the gap path
   *    covers anything pruned beyond that.
   */
  authoritative?: boolean
}

export type DurableChatTurnSubscriptionLike = {
  start: () => void
  stop: () => void
  /** Skip a pending reconnect backoff and connect now (tab visible / back
   *  online). Optional so transport fakes need not implement it. */
  reconnectNow?: () => void
}

export type DurableChatTurnSubscriptionFactory = (
  options: DurableChatTurnSubscriptionOptions
) => DurableChatTurnSubscriptionLike

export type LoadStatus = (
  target: DurableChatTurnIdentity,
  afterSeq: number,
  idToken: string | undefined
) => Promise<DurableChatTurnStatusResponse>

export type DurableChatTurnRegistryDependencies = {
  baseRecoveryDelayMs?: number
  createSubscription?: DurableChatTurnSubscriptionFactory
  loadStatus?: LoadStatus
  maxBufferedEvents?: number
  maxRecoveryDelayMs?: number
  maxRetainedEvents?: number
  maxRetainedTerminalTurns?: number
  statusPollIntervalMs?: number
}

export type RegistryEntry = {
  bufferedEvents: Map<number, DurableChatTurnEvent>
  getToken: () => string | undefined
  listeners: Set<(state: DurableChatTurnRegistryState) => void>
  recovery: Promise<void> | null
  recoveryAttempts: number
  recoveryTimer: ReturnType<typeof setTimeout> | null
  state: DurableChatTurnRegistryState
  statusPolling: boolean
  subscription: DurableChatTurnSubscriptionLike
  subscriptionActive: boolean
}

export async function defaultLoadStatus(
  target: DurableChatTurnIdentity,
  afterSeq: number,
  idToken: string | undefined
): Promise<DurableChatTurnStatusResponse> {
  return getDurableChatTurnStatusApi(target.sessionId, target.turnId, afterSeq, idToken)
}

export function defaultCreateSubscription(
  options: DurableChatTurnSubscriptionOptions
): DurableChatTurnSubscriptionLike {
  return new DurableChatTurnSubscription(options)
}
